<template>
  <AuthLayout
    sidebar-title="Create a new password"
    sidebar-text="Choose a strong password you have not used before."
    :support-phone="supportPhone"
    :is-admin="isAdmin"
    title="Set new password"
    :subtitle="subtitle"
  >
    <form action="javascript:void(0)" @submit.prevent="handleSubmit">
      <AuthPasswordField
        id="reset-password"
        v-model="password"
        label="New password"
        placeholder="Enter new password"
        :error="displayedPasswordError"
        required
        @update:model-value="onPasswordInput"
      />
      <AuthPasswordMeter
        :password="password"
        :email="passwordContext.email"
        :first-name="passwordContext.firstName"
        :last-name="passwordContext.lastName"
        :additional-personal-values="passwordContext.additionalPersonalValues"
      />

      <AuthPasswordField
        id="reset-password-confirm"
        v-model="password_confirmation"
        label="Confirm password"
        placeholder="Confirm new password"
        :error="displayedConfirmationError"
        required
        @update:model-value="onConfirmationInput"
      />

      <span v-if="displayedFormError" class="auth-login__error-block" role="alert">{{ displayedFormError }}</span>

      <div class="auth-login__actions">
        <AuthButton type="submit" :loading="loading" :disabled="!canSubmit">
          Reset password
        </AuthButton>
      </div>
    </form>
  </AuthLayout>
</template>

<script setup>
import { computed, ref } from 'vue'
import AuthLayout from '../../shared/components/AuthLayout.vue'
import AuthPasswordField from '../../shared/components/AuthPasswordField.vue'
import AuthButton from '../../shared/components/AuthButton.vue'
import AuthPasswordMeter from '../../shared/components/AuthPasswordMeter.vue'
import { evaluatePassword, isPasswordValid } from '../../shared/utils/passwordPolicy.js'

const props = defineProps({
  loading:      { type: Boolean, default: false },
  error:        { type: String,  default: '' },
  passwordError: { type: String, default: '' },
  confirmationError: { type: String, default: '' },
  formError: { type: String, default: '' },
  supportPhone: { type: String,  default: '815-908-2020' },
  isAdmin:      { type: Boolean, default: false },
  passwordResetRequired: { type: Boolean, default: false },
  passwordContext: { type: Object, default: () => ({}) },
})

const emit = defineEmits(['submit', 'clear-error'])

const password = ref('')
const password_confirmation = ref('')
const localErrors = ref({
  password: '',
  password_confirmation: '',
  form: '',
})

const newPasswordError = computed(() => props.passwordError || props.error || '')
const displayedPasswordError = computed(() => localErrors.value.password || newPasswordError.value)
const displayedConfirmationError = computed(() => localErrors.value.password_confirmation || props.confirmationError)
const displayedFormError = computed(() => localErrors.value.form || props.formError)
const subtitle = computed(() => props.passwordResetRequired
  ? 'Your password now appears in a compromised password report, please set a new one today'
  : 'Your new password must be different from previously used passwords.')

const canSubmit = computed(() =>
  isPasswordValid(password.value, props.passwordContext)
  && password.value === password_confirmation.value
)

const passwordRules = computed(() => evaluatePassword(password.value, props.passwordContext))

const resetLocalErrors = () => {
  localErrors.value = {
    password: '',
    password_confirmation: '',
    form: '',
  }
}

const onPasswordInput = () => {
  localErrors.value = { ...localErrors.value, password: '', form: '' }
  emit('clear-error', 'password')
}

const onConfirmationInput = () => {
  localErrors.value = { ...localErrors.value, password_confirmation: '', form: '' }
  emit('clear-error', 'password_confirmation')
}

const handleSubmit = () => {
  emit('clear-error')
  resetLocalErrors()

  if (!isPasswordValid(password.value, props.passwordContext)) {
    localErrors.value = {
      ...localErrors.value,
      password: firstPasswordRuleError(),
    }
    return
  }

  if (password.value !== password_confirmation.value) {
    localErrors.value = {
      ...localErrors.value,
      password_confirmation: 'Passwords do not match.',
    }
    return
  }

  emit('submit', {
    password: password.value,
    password_confirmation: password_confirmation.value,
  })
}

function firstPasswordRuleError() {
  const rules = passwordRules.value

  if (!rules.hasMinLength) return 'Password must be at least 15 characters.'
  if (!rules.hasMaxLength) return 'Password must be no more than 64 characters.'
  if (!rules.hasPrintableAsciiOnly) return 'Password must use only printable ASCII characters.'
  if (!rules.hasNoSequentialCharacters) return 'Password cannot contain sequential characters.'
  if (!rules.hasNoRepeatedCharacters) return 'Password cannot contain 4 or more repeated characters.'
  if (!rules.hasNoPlatformName) return 'Password cannot contain Remoni.'
  if (!rules.hasNoPersonalData) return 'Password cannot contain your email, first name, or last name.'

  return 'Please meet all password requirements.'
}
</script>
